import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../services/api'
import { taskService } from '../services/taskService'
import { userService } from '../services/userService'
import { patientService, extractPatients } from '../services/patientService'
import { Patient } from '../types/patient'
import { Task, TaskStatus, TaskPriority } from '../types/task'
import { TaskForm } from '../components/TaskForm'
import { formatUserName } from '../utils/name'
import { formatDate } from '../utils/locale'
import './Tasks.css'

export function Tasks() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [showCreateForm, setShowCreateForm] = useState(false)
  
  // Get user ID from auth service directly as fallback (in case user context hasn't loaded yet)
  const getCurrentUserId = () => {
    if (user?.id) return user.id
    try {
      const userStr = localStorage.getItem('clnicl_user')
      if (userStr) {
        const userObj = JSON.parse(userStr)
        return userObj?.id
      }
    } catch {
      // Ignore errors
    }
    return null
  }
  
  const currentUserId = getCurrentUserId()
  
  // Initialize filters with default "Assigned To Me" if user is available
  const [filters, setFilters] = useState<{
    status?: TaskStatus
    priority?: TaskPriority
    assignedTo?: string
    patientId?: string
    search?: string
  }>(() => {
    // Get user ID inside initializer to ensure it's computed at the right time
    let userId: string | null = null
    try {
      const userStr = localStorage.getItem('clnicl_user')
      if (userStr) {
        const userObj = JSON.parse(userStr)
        userId = userObj?.id
      }
    } catch (error) {
      // Ignore errors
    }
    
    if (userId) {
      return { assignedTo: userId }
    }
    return {}
  })
  
  // Update filters if user becomes available and filters are empty (backup in case initializer didn't work)
  useEffect(() => {
    const userId = user?.id || currentUserId
    const hasAnyFilter = filters.assignedTo || filters.patientId || filters.status || filters.search
    if (userId && !hasAnyFilter) {
      setFilters({ assignedTo: userId })
    }
  }, [user?.id, currentUserId]) // Only depend on user ID, not filters
  const [selectedTasks, setSelectedTasks] = useState<string[]>([])

  const { data: tasks = [], isLoading } = useQuery<Task[]>({
    queryKey: ['tasks', filters],
    queryFn: async () => {
      // Build query parameters from filters
      const params: Record<string, string> = {}
      
      if (filters.patientId) {
        params.patientId = filters.patientId
      }
      if (filters.assignedTo) {
        params.assignedTo = filters.assignedTo
      }
      if (filters.status) {
        params.status = filters.status
      }
      
      // Use appropriate service method or getAll with params
      if (Object.keys(params).length > 0) {
        const response = await api.get<Task[]>('/tasks', { params })
        return response.data
      }
      return taskService.getAll()
    },
  })

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => userService.getAll(),
  })

  const { data: patients = [] } = useQuery({
    queryKey: ['patients'],
    queryFn: async () => {
      const result = await patientService.getAll()
      return extractPatients(result)
    },
  })

  const createMutation = useMutation({
    mutationFn: taskService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      setShowCreateForm(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Task> }) =>
      taskService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['tasks', filters] })
      
      // Snapshot the previous value
      const previousTasks = queryClient.getQueryData<Task[]>(['tasks', filters])
      
      // Optimistically update the task in the list
      if (previousTasks) {
        const now = new Date().toISOString()
        queryClient.setQueryData<Task[]>(['tasks', filters], (old) =>
          old?.map((task) => {
            if (task.id === id) {
              const updated = { ...task, ...data, updatedAt: now }
              // Set timestamps based on status changes
              if (data.status === 'in-progress' && task.status !== 'in-progress') {
                updated.startedAt = now
              }
              if ((data.status === 'completed' || data.status === 'resolved') && task.status !== 'completed' && task.status !== 'resolved') {
                updated.completedAt = now
              }
              if (data.status === 'resolved' && task.status !== 'resolved') {
                updated.resolvedAt = now
              }
              return updated
            }
            return task
          })
        )
      }
      
      return { previousTasks }
    },
    onError: (_err, _variables, context) => {
      // Roll back on error
      if (context?.previousTasks) {
        queryClient.setQueryData(['tasks', filters], context.previousTasks)
      }
    },
    onSuccess: () => {
      // Always refetch to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: taskService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      setSelectedTasks([])
    },
  })

  const assignMutation = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      taskService.assign(id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  const filteredTasks = useMemo(() => {
    let filtered = tasks

    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(
        (task) =>
          task.title.toLowerCase().includes(searchLower) ||
          task.description?.toLowerCase().includes(searchLower)
      )
    }

    if (filters.priority) {
      filtered = filtered.filter((task) => task.priority === filters.priority)
    }

    return filtered
  }, [tasks, filters])

  const handleCreate = (formData: any) => {
    createMutation.mutate(formData)
  }

  const handleBulkAssign = (userId: string) => {
    selectedTasks.forEach((taskId) => {
      assignMutation.mutate({ id: taskId, userId })
    })
    setSelectedTasks([])
  }

  const handleBulkStatusChange = (status: TaskStatus) => {
    selectedTasks.forEach((taskId) => {
      updateMutation.mutate({ id: taskId, data: { status } })
    })
    setSelectedTasks([])
  }

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      deleteMutation.mutate(id)
    }
  }

  const getUserName = (userId?: string) => {
    if (!userId) return 'Unassigned'
    const user = users.find((u) => u.id === userId)
    return user ? formatUserName(user as any) : userId
  }

  const getPatientName = (patientId?: string) => {
    if (!patientId) return null
    const patient = patients.find((p: Patient) => p.id === patientId)
    return patient ? `${patient.firstName} ${patient.lastName}` : patientId
  }

  if (isLoading) {
    return <div className="tasks-loading">Loading tasks...</div>
  }

  return (
    <div className="tasks-page">
      <div className="tasks-header">
        <h1>Clinical Tasks</h1>
        <button
          className="btn-create-task"
          onClick={() => setShowCreateForm(true)}
        >
          + New Task
        </button>
      </div>

      {showCreateForm && (
        <div className="tasks-create-form-container">
          <TaskForm
            onSubmit={handleCreate}
            onCancel={() => setShowCreateForm(false)}
          />
        </div>
      )}

      <div className="tasks-filters">
        <input
          type="text"
          placeholder="Search tasks..."
          value={filters.search || ''}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="tasks-search"
        />

        <select
          value={filters.status || ''}
          onChange={(e) =>
            setFilters({
              ...filters,
              status: e.target.value ? (e.target.value as TaskStatus) : undefined,
            })
          }
        >
          <option value="">All Statuses</option>
          <option value="created">Created</option>
          <option value="assigned">Assigned</option>
          <option value="unassigned">Unassigned</option>
          <option value="in-progress">In Progress</option>
          <option value="performed">Performed</option>
          <option value="held">Held</option>
          <option value="pended">Pended</option>
          <option value="denied">Denied</option>
          <option value="resolved">Resolved</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <select
          value={filters.priority || ''}
          onChange={(e) =>
            setFilters({
              ...filters,
              priority: e.target.value ? (e.target.value as TaskPriority) : undefined,
            })
          }
        >
          <option value="">All Priorities</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>

        <select
          value={filters.assignedTo === currentUserId ? 'me' : (filters.assignedTo || '')}
          onChange={(e) => {
            const value = e.target.value
            const userId = user?.id || currentUserId
            if (value === 'me') {
              setFilters({
                ...filters,
                assignedTo: userId || undefined,
                patientId: undefined, // Clear patient filter when changing assignee (mutually exclusive)
              })
            } else if (value === '') {
              setFilters({
                ...filters,
                assignedTo: undefined,
                patientId: undefined, // Clear patient filter when changing assignee (mutually exclusive)
              })
            } else {
              setFilters({
                ...filters,
                assignedTo: value,
                patientId: undefined, // Clear patient filter when changing assignee (mutually exclusive)
              })
            }
          }}
        >
          <option value="">All Assignees</option>
          {currentUserId && (
            <option value="me">Assigned To Me</option>
          )}
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {formatUserName(u as any)}
            </option>
          ))}
        </select>

        <select
          value={filters.patientId || ''}
          onChange={(e) =>
            setFilters({
              ...filters,
              patientId: e.target.value || undefined,
            })
          }
        >
          <option value="">All Patients</option>
          {patients.slice(0, 100).map((patient: Patient) => (
            <option key={patient.id} value={patient.id}>
              {patient.firstName} {patient.lastName}
            </option>
          ))}
        </select>
      </div>

      {selectedTasks.length > 0 && (
        <div className="tasks-bulk-actions">
          <span>{selectedTasks.length} task(s) selected</span>
          <select
            onChange={(e) => {
              if (e.target.value) {
                handleBulkAssign(e.target.value)
              }
            }}
          >
            <option value="">Assign to...</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {formatUserName(user as any)}
              </option>
            ))}
          </select>
          <select
            onChange={(e) => {
              if (e.target.value) {
                handleBulkStatusChange(e.target.value as TaskStatus)
              }
            }}
          >
            <option value="">Change status to...</option>
            <option value="assigned">Assigned</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button onClick={() => setSelectedTasks([])}>Clear Selection</button>
        </div>
      )}

      <div className="tasks-list">
        {filteredTasks.length === 0 ? (
          <div className="tasks-empty">No tasks found</div>
        ) : (
          <table className="tasks-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={
                      selectedTasks.length === filteredTasks.length &&
                      filteredTasks.length > 0
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedTasks(filteredTasks.map((t) => t.id))
                      } else {
                        setSelectedTasks([])
                      }
                    }}
                  />
                </th>
                <th>Title</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Assigned To</th>
                <th>Patient</th>
                <th>Due Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map((task) => (
                <tr key={task.id} className={task.status === 'completed' ? 'completed' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedTasks.includes(task.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTasks([...selectedTasks, task.id])
                        } else {
                          setSelectedTasks(selectedTasks.filter((id) => id !== task.id))
                        }
                      }}
                    />
                  </td>
                  <td>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        navigate(`/tasks/${task.id}`)
                      }}
                    >
                      {task.title}
                    </a>
                  </td>
                  <td>
                    <span className={`task-status-badge status-${task.status}`}>
                      {task.status}
                    </span>
                  </td>
                  <td>
                    <span className={`task-priority-badge priority-${task.priority}`}>
                      {task.priority}
                    </span>
                  </td>
                  <td>{getUserName(task.assignedTo)}</td>
                  <td>
                    {task.patientId ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault()
                          navigate(`/patients/${task.patientId}`)
                        }}
                      >
                        {getPatientName(task.patientId)}
                      </a>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    {task.dueDate ? (
                      <span
                        className={
                          new Date(task.dueDate) < new Date() &&
                          task.status !== 'completed'
                            ? 'overdue'
                            : ''
                        }
                      >
                        {formatDate(task.dueDate)}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    <button
                      onClick={() => navigate(`/tasks/${task.id}`)}
                      className="btn-view"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleDelete(task.id)}
                      className="btn-delete"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

